import { Custom } from "components";
import { useApp } from "context";
import { useForm } from "hooks";
import { Constants, Enums, Helpers, Store } from "utils";
import { Grid } from "@mui/material";
import { Feedback, PairValue } from "types";
import { useState, useEffect } from "react";
import { IPatient } from "interfaces";

type Props = {
  isClinic: boolean;
  patient: IPatient;
  requiredOnly?: boolean;
  onSubmit?: () => void;
};

export const Profile = (props: Props) => {
  const { user, service, setFeedback, t, theme } = useApp();

  const fromJSON = {
    ...props.patient,
    dob: props.patient.dob ?? "",
    firstName: props.patient.username?.split(",")[0] ?? "",
    lastName: props.patient.username?.split(",")[1] ?? "",
    hcId: props.patient.hcId ?? "",
    hcExpiry: props.patient.hcExpiry ?? "",
    city: props.patient.city ?? "",
    postalCode: props.patient.postalCode ?? "",
    imageUrl: props.patient.imageUrl ?? "",
    patientAddress: props.patient.patientAddress ?? "",
    preferredPharmacy: props.patient.preferredPharmacy ?? "",
    policyNumber: props.patient.policyNumber ?? "",
    phone: props.patient.phone ?? "",
    email: props.patient.email ?? "",
    privateInsuranceProvider: props.patient.privateInsuranceProvider ?? "",
    gender: Helpers.hasValue(props.patient.gender)
      ? props.patient.gender === t.label.female
        ? Enums.EnumGender.Female
        : Enums.EnumGender.Male
      : "",
  } as any;

  const validation = {
    hcId: {
      valid: { error: false, helperText: "" },
      invalid: { error: true, helperText: "Example: 1234-123-123-AB" },
      callback: (value: string) => /^(\d{10}[a-zA-Z]{2})?$/.test(value),
    },
    hcExpiry: {
      valid: { error: false, helperText: "" },
      invalid: { error: true, helperText: "Invalid Date" },
      callback: (value: string) =>
        new Date(value).toString() !== "Invalid Date",
    },
    dob: {
      valid: { error: false, helperText: "" },
      invalid: { error: true, helperText: "Invalid Date" },
      callback: (value: string) =>
        new Date(value).toString() !== "Invalid Date",
    },
    phone: {
      valid: { error: false, helperText: "" },
      invalid: { error: true, helperText: "Example: (647) 123-1234" },
      callback: (value: string) => /^(\d{10})$/.test(value),
    },
    postalCode: {
      valid: { error: false, helperText: "" },
      invalid: { error: true, helperText: "Example: M1M 1N1" },
      callback: (value: string) =>
        /^([a-zA-Z]\d[a-zA-Z]\d[a-zA-Z]\d)$/.test(value),
    },
  };

  const [validationState, setValidationState] = useState({
    hcId: validation.hcId.valid,
    hcExpiry: validation.hcExpiry.valid,
    dob: validation.dob.valid,
    phone: validation.phone.valid,
    postalCode: validation.postalCode.valid,
  });

  const [state, setState] = useState<IPatient | any>(fromJSON);
  const { onChange, onDropdownChange, onSubmit } = useForm(
    submitCallback,
    {},
    updateCallback
  );

  useEffect(() => setState(fromJSON), [props.patient]);

  function toJSON(patient: any) {
    return Helpers.removeFromObject({
      ...patient,
      ...(props.isClinic
        ? { patientId: props.patient.patientId }
        : { patientId: null, clinicId: null, hcValid: null, active: null }),
      username:
        Helpers.hasValue(patient.firstName) &&
        Helpers.hasValue(patient.lastName)
          ? `${patient.firstName},${patient.lastName}`
          : "",
      hcId: Helpers.hasValue(patient.hcId)
        ? Helpers.onlyWord(patient.hcId).toUpperCase()
        : "",
      hcExpiry: Helpers.hasValue(patient.hcExpiry)
        ? patient.hcExpiry.split("T")[0]
        : Constants.MIN_DATE,
      phone: Helpers.hasValue(patient.phone)
        ? Helpers.onlyNumber(patient.phone)
        : "",
      postalCode: Helpers.hasValue(patient.postalCode)
        ? patient.postalCode.toUpperCase().replace(/\s/g, "")
        : "",
      dob: Helpers.hasValue(patient.dob) ? patient.dob.split("T")[0] : "",
      gender: Helpers.hasValue(patient.gender)
        ? patient.gender === Enums.EnumGender.Female
          ? t.label.female
          : t.label.male
        : "",
    } as any);
  }

  function updateCallback(name: string, value: any) {
    if (name in state) {
      setState({ ...state, [name]: value });
    }
  }

  async function submitCallback() {
    const final = toJSON(state);

    let newValidationState: any = {};

    let valid = true;

    let name: keyof typeof validation;
    for (name in validation) {
      if (final.hasOwnProperty(name)) {
        if (validation[name].callback((final as any)[name])) {
          newValidationState[name] = validation[name].valid;
        } else {
          newValidationState[name] = validation[name].invalid;
          valid = false;
        }
      }
    }

    setValidationState(newValidationState);

    if (!valid) return;

    const [status, response] = await service.patient.update(toJSON(state));
    if (status === Enums.EnumResponse.Success) {
      setFeedback({
        message: t.message.feedback.request_success,
        severity: Enums.EnumFeedback.Success,
      } as Feedback);
      props.onSubmit && props.onSubmit();
      Store.sessionStorage.set({ completed: true });
    } else {
      setFeedback({
        message: t.message.feedback.request_error,
        severity: Enums.EnumFeedback.Error,
      } as Feedback);
    }
  }

  const copyAll = () => {
    const { dob, hcExpiry, gender } = state;

    const cb = {
      ...state,
      dob: dob.slice(5, 7) + dob.slice(8, 10) + dob.slice(0, 4),
      hcExpiry: hcExpiry
        ? hcExpiry.slice(5, 7) + hcExpiry.slice(8, 10) + hcExpiry.slice(0, 4)
        : "",
      gender: gender
        ? gender === Enums.EnumGender.Female
          ? "{Up}{Up}"
          : "{Up}{Up}{Up}"
        : "{Up}",
    };

    navigator.clipboard.writeText(JSON.stringify(cb));
  };

  return (
    <>
      <form onSubmit={onSubmit} className="qh-form">
        <Grid container spacing={theme.margin.sm}>
          <Grid item xs={12} sm={6}>
            <Custom.TextField
              name="lastName"
              clipboard={props.isClinic}
              required={true}
              onChange={onChange}
              label={t.label.last_name}
              value={state.lastName}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Custom.TextField
              name="firstName"
              clipboard={props.isClinic}
              required={true}
              onChange={onChange}
              label={t.label.first_name}
              value={state.firstName}
            />
          </Grid>
          {!props.requiredOnly && (
            <>
              <Grid item xs={12} sm={6}>
                <Custom.TextField
                  name="hcId"
                  error={validationState.hcId.error}
                  helperText={validationState.hcId.helperText}
                  clipboard={props.isClinic}
                  mask={Constants.INPUT_MASK.HC_ID}
                  onChange={onChange}
                  label={t.label.health_number}
                  value={state.hcId}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Custom.TextField
                  name="hcExpiry"
                  error={validationState.hcExpiry.error}
                  helperText={validationState.hcExpiry.helperText}
                  clipboard={props.isClinic}
                  mask={Constants.INPUT_MASK.DATE}
                  maskPlaceholder={Constants.INPUT_MASK_PLACEHOLDER.DATE}
                  onChange={onChange}
                  label={t.label.expiry_date}
                  value={state.hcExpiry}
                />
              </Grid>
            </>
          )}
          <Grid item xs={12} sm={6}>
            <Custom.TextField
              name="phone"
              error={validationState.phone.error}
              helperText={validationState.phone.helperText}
              clipboard={props.isClinic}
              required={true}
              onChange={onChange}
              label={t.label.phone}
              mask={Constants.INPUT_MASK.PHONE}
              value={state.phone}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Custom.TextField
              name="email"
              clipboard={props.isClinic}
              disabled={true}
              onChange={onChange}
              label={t.label.email}
              value={state.email}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Custom.TextField
              name="dob"
              required={true}
              error={validationState.dob.error}
              helperText={validationState.dob.helperText}
              clipboard={props.isClinic}
              onChange={onChange}
              label={t.label.dob}
              mask={Constants.INPUT_MASK.DATE}
              maskPlaceholder={Constants.INPUT_MASK_PLACEHOLDER.DATE}
              value={state.dob}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Custom.Select
              name="gender"
              required={true}
              label={t.label.gender}
              value={state.gender}
              items={[
                {
                  key: Enums.EnumGender.Female,
                  value: t.label.female,
                } as PairValue,
                {
                  key: Enums.EnumGender.Male,
                  value: t.label.male,
                } as PairValue,
              ]}
              onDropdownChange={onDropdownChange}
            />
          </Grid>
          <Grid item xs={12}>
            <Custom.TextField
              name="patientAddress"
              clipboard={props.isClinic}
              required={true}
              onChange={onChange}
              label={t.label.address}
              value={state.patientAddress}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Custom.TextField
              name="city"
              clipboard={props.isClinic}
              required={true}
              onChange={onChange}
              label={t.label.city}
              value={state.city}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Custom.TextField
              name="postalCode"
              error={validationState.postalCode.error}
              helperText={validationState.postalCode.helperText}
              clipboard={props.isClinic}
              required={true}
              onChange={onChange}
              mask={Constants.INPUT_MASK.POSTAL_CODE}
              label={t.label.postal_code}
              value={state.postalCode}
            />
          </Grid>
          {!props.requiredOnly && (
            <>
              <Grid item xs={12} sm={6}>
                <Custom.TextField
                  name="privacyInsuranceProvider"
                  clipboard={props.isClinic}
                  onChange={onChange}
                  label={t.label.insurance_provider}
                  value={state.privacyInsuranceProvider}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Custom.TextField
                  name="policyNumber"
                  clipboard={props.isClinic}
                  onChange={onChange}
                  label={t.label.policy_number}
                  value={state.policyNumber}
                />
              </Grid>
              <Grid item xs={12}>
                <Custom.TextField
                  name="preferredPharmacy"
                  clipboard={props.isClinic}
                  onChange={onChange}
                  label={t.label.preferred_pharmacy}
                  value={state.preferredPharmacy}
                />
              </Grid>
            </>
          )}
          <Grid item xs={12}>
            <div className="qh-control">
              {props.isClinic && (
                <Custom.Button variant="outlined" onClick={copyAll}>
                  {t.action.copy_all}
                </Custom.Button>
              )}
              <Custom.Button submit>{t.action.submit}</Custom.Button>
            </div>
          </Grid>
        </Grid>
      </form>
    </>
  );
};
